import React, { useState, useRef, useEffect, useCallback } from 'react';

function PreviewModal({ stall, onClose, onSelect }) {
  const [activeImg, setActiveImg] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [logos, setLogos] = useState([]);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const canvasRef = useRef(null);
  const fsCanvasRef = useRef(null);
  const stallImgRef = useRef(null);
  const logoImgRefs = useRef([]);
  const logosRef = useRef([]);
  const selectedLogoRef = useRef(null);

  const images = stall.images || [];

  // Keep refs in sync
  useEffect(() => { logosRef.current = logos; }, [logos]);
  useEffect(() => { selectedLogoRef.current = selectedLogo; }, [selectedLogo]);

  const drawOnCanvas = useCallback((canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (stallImgRef.current) {
      ctx.drawImage(stallImgRef.current, 0, 0, canvas.width, canvas.height);
    }
    logosRef.current.forEach((logo, i) => {
      const img = logoImgRefs.current[i];
      if (img) {
        const x = (logo.pos.x / 100) * canvas.width;
        const y = (logo.pos.y / 100) * canvas.height;
        ctx.drawImage(img, x, y, logo.size, logo.size);
        if (i === selectedLogoRef.current) {
          ctx.strokeStyle = '#5B4FCF';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(x, y, logo.size, logo.size);
          ctx.setLineDash([]);
        }
      }
    });
  }, []);

  const redraw = useCallback(() => {
    drawOnCanvas(canvasRef.current);
    if (fsCanvasRef.current) drawOnCanvas(fsCanvasRef.current);
  }, [drawOnCanvas]);

  // Load stall image on activeImg change
  useEffect(() => {
    if (!images[activeImg]) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = images[activeImg];
    img.onload = () => { stallImgRef.current = img; redraw(); };
  }, [activeImg]); // eslint-disable-line

  // Redraw when logos or selection changes
  useEffect(() => { redraw(); }, [logos, selectedLogo, redraw]);

  // Init fullscreen canvas
  useEffect(() => {
    if (fullscreen && fsCanvasRef.current) {
      fsCanvasRef.current.width = 1200;
      fsCanvasRef.current.height = 680;
      redraw();
    }
  }, [fullscreen, redraw]);

  const handleFile = (e) => {
    Array.from(e.target.files).forEach((f) => {
      const r = new FileReader();
      r.onload = (ev) => {
        const src = ev.target.result;
        const img = new Image();
        img.src = src;
        img.onload = () => {
          const newIdx = logoImgRefs.current.length;
          logoImgRefs.current[newIdx] = img;
          setLogos((prev) => {
            const updated = [...prev, { src, pos: { x: 5 + prev.length * 6, y: 5 + prev.length * 6 }, size: 100 }];
            logosRef.current = updated;
            setSelectedLogo(updated.length - 1);
            selectedLogoRef.current = updated.length - 1;
            redraw();
            return updated;
          });
        };
      };
      r.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const updateLogoSize = (idx, size) => {
    setLogos((prev) => {
      const updated = prev.map((l, i) => i === idx ? { ...l, size } : l);
      logosRef.current = updated;
      setTimeout(() => {
        drawOnCanvas(canvasRef.current);
        if (fsCanvasRef.current) drawOnCanvas(fsCanvasRef.current);
      }, 0);
      return updated;
    });
  };

  const removeLogo = (idx) => {
    logoImgRefs.current.splice(idx, 1);
    setLogos((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      logosRef.current = updated;
      redraw();
      return updated;
    });
    setSelectedLogo(null);
    selectedLogoRef.current = null;
  };

  const getCanvasCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    return {
      mx: ((e.clientX - rect.left) / rect.width) * 100,
      my: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  const onMouseDown = (e, canvas) => {
    if (!canvas) return;
    const { mx, my } = getCanvasCoords(e, canvas);
    for (let i = logosRef.current.length - 1; i >= 0; i--) {
      const logo = logosRef.current[i];
      const logoW = (logo.size / canvas.width) * 100;
      const logoH = (logo.size / canvas.height) * 100;
      if (mx >= logo.pos.x && mx <= logo.pos.x + logoW && my >= logo.pos.y && my <= logo.pos.y + logoH) {
        setSelectedLogo(i);
        selectedLogoRef.current = i;
        setDragOffset({ x: mx - logo.pos.x, y: my - logo.pos.y });
        setDragging(true);
        redraw();
        return;
      }
    }
    setSelectedLogo(null);
    selectedLogoRef.current = null;
    redraw();
  };

  const onMouseMove = useCallback((e) => {
    if (!dragging || selectedLogoRef.current === null) return;
    const canvas = fsCanvasRef.current && fullscreen ? fsCanvasRef.current : canvasRef.current;
    if (!canvas) return;
    const { mx, my } = getCanvasCoords(e, canvas);
    setLogos((prev) => {
      const updated = prev.map((l, i) => i === selectedLogoRef.current
        ? { ...l, pos: { x: Math.max(0, Math.min(85, mx - dragOffset.x)), y: Math.max(0, Math.min(85, my - dragOffset.y)) } }
        : l
      );
      logosRef.current = updated;
      drawOnCanvas(canvasRef.current);
      if (fsCanvasRef.current) drawOnCanvas(fsCanvasRef.current);
      return updated;
    });
  }, [dragging, dragOffset, fullscreen, drawOnCanvas]);

  const onMouseUp = () => setDragging(false);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging, onMouseMove]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { if (fullscreen) setFullscreen(false); else onClose(); }
      if (e.key === 'ArrowRight') setActiveImg((i) => (i + 1) % images.length);
      if (e.key === 'ArrowLeft') setActiveImg((i) => (i - 1 + images.length) % images.length);
      if (e.key === 'Delete' && selectedLogoRef.current !== null) removeLogo(selectedLogoRef.current);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, images.length, fullscreen]); // eslint-disable-line

  const canvasStyle = { width: '100%', height: 'auto', display: 'block', cursor: dragging ? 'grabbing' : (logos.length ? 'grab' : 'default'), userSelect: 'none' };

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="slide-in" style={{ background: '#fff', borderRadius: 20, width: 'min(960px, 95vw)', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.2)' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.5rem 2rem', borderBottom: '1px solid #f0ede8', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: stall.accent, marginBottom: 4 }}>{stall.id} · {stall.type}</div>
              <h2 style={{ fontFamily: 'DM Serif Display', fontSize: 24, color: '#1a1a1a' }}>{stall.name}</h2>
            </div>
            <button onClick={onClose} style={{ background: '#f5f3f0', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#666', cursor: 'pointer' }}>Close ✕</button>
          </div>

          {/* Body */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px' }}>
            <div style={{ padding: '2rem' }}>

              {/* Canvas */}
              <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: stall.color }}>
                <canvas ref={canvasRef} width={600} height={340} onMouseDown={(e) => onMouseDown(e, canvasRef.current)} style={canvasStyle} />

                {images.length > 1 && (
                  <>
                    <button onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)}
                      style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>‹</button>
                    <button onClick={() => setActiveImg((i) => (i + 1) % images.length)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>›</button>
                    <div style={{ position: 'absolute', bottom: 10, right: 12, background: 'rgba(0,0,0,0.45)', borderRadius: 20, padding: '3px 10px', fontSize: 11, color: '#fff' }}>{activeImg + 1} / {images.length}</div>
                  </>
                )}

                <button onClick={() => setFullscreen(true)}
                  style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: 6, padding: '5px 10px', color: '#fff', fontSize: 13, cursor: 'pointer' }}>⛶</button>

                {logos.length > 0 && (
                  <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(255,255,255,0.8)', borderRadius: 6, padding: '3px 10px', fontSize: 10, color: '#666' }}>
                    Click logo · Drag to move · Del to remove
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {images.map((img, i) => (
                    <div key={i} onClick={() => setActiveImg(i)}
                      style={{ width: 72, height: 52, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', flexShrink: 0, border: i === activeImg ? `2.5px solid ${stall.accent}` : '2.5px solid transparent', opacity: i === activeImg ? 1 : 0.6, transition: 'all 0.2s' }}>
                      <img src={img} alt={`view ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Upload */}
              <div style={{ marginTop: '1.5rem' }}>
                <label style={{ display: 'block', border: '1.5px dashed #c0bdb8', borderRadius: 10, padding: '12px 20px', textAlign: 'center', cursor: 'pointer', color: '#888', fontSize: 13 }}>
                  <span style={{ display: 'block', fontSize: 20, marginBottom: 4 }}>↑</span>
                  Add Logo(s) — select multiple files
                  <input type="file" accept="image/*" multiple onChange={handleFile} style={{ display: 'none' }} />
                </label>
              </div>

              {/* Logo list */}
              {logos.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {logos.map((logo, i) => (
                    <div key={i} onClick={() => { setSelectedLogo(i); selectedLogoRef.current = i; redraw(); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10, border: `1.5px solid ${selectedLogo === i ? stall.accent : '#e8e4e0'}`, background: selectedLogo === i ? stall.color : '#faf9f7', cursor: 'pointer' }}>
                      <img src={logo.src} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Logo {i + 1} — {logo.size}px</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); updateLogoSize(i, Math.max(30, logo.size - 10)); }}
                            style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >−</button>
                          <input
                            type="range" min="30" max="250" value={logo.size}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); updateLogoSize(i, +e.target.value); }}
                            style={{ flex: 1, accentColor: stall.accent }}
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); updateLogoSize(i, Math.min(250, logo.size + 10)); }}
                            style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >+</button>
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); removeLogo(i); }}
                        style={{ background: 'none', border: 'none', color: '#ccc', fontSize: 16, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right sidebar */}
            <div style={{ background: '#faf9f7', borderLeft: '1px solid #f0ede8', padding: '2rem', borderRadius: '0 0 20px 0' }}>
              <h4 style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#999', marginBottom: 16 }}>Stall Details</h4>
              {[['Stall ID', stall.id], ['Type', stall.type], ['Area', stall.area + 'm²'], ['Size', stall.size], ['Category', stall.category]].map(([label, value]) => (
                <div key={label} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', textTransform: 'capitalize' }}>{value}</div>
                </div>
              ))}
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #eceae6' }}>
                <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>Description</div>
                <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>{stall.desc}</p>
              </div>
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #eceae6' }}>
                <div style={{ fontSize: 11, color: '#aaa', marginBottom: 10 }}>Tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {stall.tags.map((t) => (
                    <span key={t} style={{ fontSize: 10, background: stall.color, color: stall.accent, borderRadius: 6, padding: '3px 8px', fontWeight: 500 }}>{t}</span>
                  ))}
                </div>
              </div>
              <button onClick={() => onSelect(stall, logos)} style={{ marginTop: 24, width: '100%', background: stall.accent, border: 'none', borderRadius: 10, padding: '12px', color: '#fff', fontSize: 13, fontWeight: 500, letterSpacing: 0.5, cursor: 'pointer' }}>
                Select This Design
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen */}
      {fullscreen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setFullscreen(false); }}
        >
          <canvas
            ref={fsCanvasRef}
            width={1200}
            height={680}
            onMouseDown={(e) => onMouseDown(e, fsCanvasRef.current)}
            style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: 10, cursor: dragging ? 'grabbing' : (logos.length ? 'grab' : 'default'), userSelect: 'none' }}
          />
          <button onClick={() => setFullscreen(false)}
            style={{ position: 'absolute', top: 20, right: 24, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', fontSize: 14, cursor: 'pointer' }}>
            ✕ Close
          </button>
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + images.length) % images.length); }}
                style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 48, height: 48, fontSize: 24, color: '#fff', cursor: 'pointer' }}>‹</button>
              <button onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % images.length); }}
                style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 48, height: 48, fontSize: 24, color: '#fff', cursor: 'pointer' }}>›</button>
              <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                {activeImg + 1} / {images.length}
              </div>
            </>
          )}
          {logos.length > 0 && (
            <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 10, background: 'rgba(0,0,0,0.6)', borderRadius: 12, padding: '10px 16px', alignItems: 'center' }}>
              {logos.map((logo, i) => (
                <div key={i} onClick={() => { setSelectedLogo(i); selectedLogoRef.current = i; drawOnCanvas(canvasRef.current); drawOnCanvas(fsCanvasRef.current); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, border: `1.5px solid ${selectedLogo === i ? '#fff' : 'rgba(255,255,255,0.2)'}`, cursor: 'pointer', background: selectedLogo === i ? 'rgba(255,255,255,0.15)' : 'transparent' }}>
                  <img src={logo.src} alt="" style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 4 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>Logo {i + 1} — {logo.size}px</div>
                    <input
                      type="range" min="30" max="250" value={logo.size}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); updateLogoSize(i, +e.target.value); }}
                      style={{ width: 120, accentColor: '#fff' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default PreviewModal;
